<template>
  <div class="search-toolbar">
    <div class="quick-search-combo">
      <el-select
        :model-value="quickField"
        :placeholder="choosePlaceholder"
        class="quick-search-combo__field"
        :style="quickFieldWidthStyle"
        :teleported="teleported"
        popper-class="quick-search-combo__menu"
        @update:model-value="handleQuickFieldUpdate"
        @change="handleQuickFieldChange">
        <el-option
          v-for="item in quickFieldOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value" />
      </el-select>
      <!-- 值区域：select 支持字段级 filterable（如租户可搜索） -->
      <slot
        v-if="$slots['quick-value'] && currentQuickField.type === 'slot'"
        name="quick-value"
        :field="currentQuickField"
        :value="quickMultipleSelectValue"
        :update-value="handleQuickValueUpdate"
        :change-value="handleQuickValueChange" />
      <el-select
        v-else-if="isMultipleSelectField"
        :key="`${currentQuickField.key}-multiple`"
        :model-value="quickMultipleSelectValue"
        :placeholder="quickSearchPlaceholder"
        multiple
        :clearable="currentQuickField.clearable !== false"
        :filterable="currentQuickField.filterable === true"
        :teleported="teleported"
        collapse-tags
        class="quick-search-combo__value quick-search-combo__value--select"
        @update:model-value="handleQuickValueUpdate"
        @change="handleQuickValueChange">
        <el-option
          v-for="option in currentQuickField.options || []"
          :key="`${currentQuickField.key}-${option.value}`"
          :label="option.label"
          :value="option.value" />
      </el-select>
      <el-select
        v-else-if="isSingleSelectField"
        :key="`${currentQuickField.key}-single`"
        :model-value="quickSingleSelectValue"
        :placeholder="quickSearchPlaceholder"
        :clearable="currentQuickField.clearable !== false"
        :filterable="currentQuickField.filterable === true"
        :teleported="teleported"
        class="quick-search-combo__value quick-search-combo__value--select"
        @update:model-value="handleQuickValueUpdate"
        @change="handleQuickValueChange">
        <el-option
          v-for="option in currentQuickField.options || []"
          :key="`${currentQuickField.key}-${option.value}`"
          :label="option.label"
          :value="option.value" />
      </el-select>
      <el-input
        v-else
        :key="`${currentQuickField.key}-input`"
        :model-value="quickValue"
        :placeholder="quickSearchPlaceholder"
        class="quick-search-combo__value"
        clearable
        @update:model-value="handleQuickValueUpdate"
        @input="handleQuickInput" />
    </div>

    <div class="search-toolbar__action">
      <el-tooltip :show-after="500" :content="advancedButtonText" placement="top" effect="light">
        <el-button
          class="advanced-filter-trigger"
          :class="{ 'is-active': searchMode === 'advanced' && !!filterSummary }"
          :icon="Filter"
          @click="emit('open-advanced')" />
      </el-tooltip>
    </div>

    <div v-if="searchMode === 'advanced' && filterSummary" class="filter-summary-inline">
      <div class="filter-summary">
        <span class="filter-summary__label">{{ advancedButtonText }}:</span>
        <el-tooltip :show-after="500" :content="filterSummary" placement="top" effect="light">
          <span class="filter-summary__content">{{ filterSummary }}</span>
        </el-tooltip>
        <el-button class="filter-summary__clear" @click="emit('reset-search')">
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </div>

    <div v-if="showRefresh" class="search-toolbar__action">
      <el-tooltip :show-after="700" :content="refreshText" placement="top" effect="light">
        <el-button class="refresh-square-button" :icon="Refresh" @click="emit('refresh')" />
      </el-tooltip>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
// 列表页通用搜索栏：快捷单字段搜索 + 高级筛选入口 + 刷新
import { computed } from 'vue'
import { Close, Filter, Refresh } from '@element-plus/icons-vue'

const props = defineProps({
  quickFields: {
    type: Object,
    default: () => ({})
  },
  quickField: {
    type: String,
    default: ''
  },
  quickValue: {
    type: [String, Number, Array],
    default: ''
  },
  defaultQuickField: {
    type: String,
    default: ''
  },
  searchMode: {
    type: String,
    default: 'quick'
  },
  filterSummary: {
    type: String,
    default: ''
  },
  advancedButtonText: {
    type: String,
    default: ''
  },
  choosePlaceholder: {
    type: String,
    default: ''
  },
  searchPlaceholder: {
    type: String,
    default: ''
  },
  refreshText: {
    type: String,
    default: ''
  },
  showRefresh: {
    type: Boolean,
    default: true
  },
  teleported: {
    type: Boolean,
    default: true
  },
  // 左侧字段下拉宽度，默认 144px，可传数字或带单位字符串
  quickFieldWidth: {
    type: [Number, String],
    default: 144
  }
})

const emit = defineEmits([
  'update:quickField',
  'update:quickValue',
  'quick-field-change',
  'quick-value-change',
  'quick-input',
  'open-advanced',
  'reset-search',
  'refresh'
])

const quickFieldOptions = computed(() => {
  return Object.values(props.quickFields || {}).map(field => ({
    label: field.label,
    value: field.key
  }))
})

const fallbackQuickField = computed(() => {
  if (props.defaultQuickField && props.quickFields?.[props.defaultQuickField]) return props.defaultQuickField
  return quickFieldOptions.value[0]?.value || ''
})

const currentQuickField = computed(() => {
  return props.quickFields?.[props.quickField] || props.quickFields?.[fallbackQuickField.value] || {}
})

const isSingleSelectField = computed(() => {
  return currentQuickField.value.type === 'select'
})

const isMultipleSelectField = computed(() => {
  return currentQuickField.value.type === 'select-multiple'
})

const quickSingleSelectValue = computed(() => {
  return Array.isArray(props.quickValue) ? '' : props.quickValue
})

const quickMultipleSelectValue = computed(() => {
  return Array.isArray(props.quickValue) ? props.quickValue : []
})

const quickSearchPlaceholder = computed(() => {
  return currentQuickField.value?.placeholder || props.searchPlaceholder
})

const quickFieldWidthStyle = computed(() => {
  const width = props.quickFieldWidth
  return {
    width: typeof width === 'number' ? `${width}px` : width
  }
})

const handleQuickFieldUpdate = (value) => {
  emit('update:quickField', value)
}

const handleQuickFieldChange = (value) => {
  emit('quick-field-change', value)
}

const handleQuickValueUpdate = (value) => {
  emit('update:quickValue', value)
}

const handleQuickValueChange = (value) => {
  emit('quick-value-change', value)
}

const handleQuickInput = (value) => {
  emit('quick-input', value)
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.search-toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.quick-search-combo {
  display: flex;
  align-items: stretch;
}

.quick-search-combo :deep(.el-input__wrapper) {
  border-radius: 0;
}

.quick-search-combo__field {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.quick-search-combo__field :deep(.el-input__wrapper) {
  border-right: 0 !important;
  border-radius: 2px 0 0 2px !important;
}

.quick-search-combo__field :deep(.el-input__inner) {
  cursor: pointer;
  color: $grey-9;
}

.quick-search-combo__value {
  width: 187px;
  margin-left: -1px;
  position: relative;
  z-index: 0;
}

.quick-search-combo__value :deep(.el-input__wrapper) {
  border-radius: 0 2px 2px 0 !important;
}

.quick-search-combo__value:focus-within {
  z-index: 2;
}

.search-toolbar__action {
  margin-left: 0 !important;
}

.filter-summary-inline {
  min-width: 0;
  display: flex;
  align-items: center;
  margin-left: -6px;
}

.filter-summary {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 36px;
  max-width: 420px;
  padding-left: 12px;
  border: 1px solid $grey-4;
  border-radius: 2px;
  background: #fafbfd;
}

.filter-summary__label {
  flex-shrink: 0;
  margin-right: 8px;
  color: $grey-7;
  font-weight: 400;
}

.filter-summary__content {
  overflow: hidden;
  max-width: 300px;
  color: $grey-9;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.filter-summary__clear {
  height: 34px !important;
  margin-left: 10px !important;
  border: 0 !important;
  border-left: 1px solid $grey-4 !important;
  border-radius: 0 2px 2px 0 !important;
  padding: 0 12px !important;
  background: transparent !important;
  color: $grey-7 !important;
}

.filter-summary__clear :deep(.el-icon) {
  margin-right: 0 !important;
}

.filter-summary__clear:hover,
.filter-summary__clear:focus {
  background: rgba(64, 158, 255, 0.08) !important;
  color: var(--el-color-primary) !important;
}

.advanced-filter-trigger {
  width: 36px;
  height: 36px !important;
  padding: 0 !important;
}

.advanced-filter-trigger :deep(.el-icon) {
  margin-right: 0 !important;
  font-size: 15px;
}

.advanced-filter-trigger.is-active {
  border-color: var(--el-color-primary) !important;
  background: rgba(64, 158, 255, 0.08) !important;
  color: var(--el-color-primary) !important;
}
</style>
